import { InitialFieldsAlignment } from "@/type/core/dashboard/dashboard.types";
import { UNIVERSIGN_RATIO } from "@/utils/Constant";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";

const PDF_VIEWER_PAGINATION_MARGIN = 16 * 2; // 1 rem top and 1 rem bottom
const PDF_BORDER_SIZE = 1 * 2; // 1px top and bottom
const INITIAL_FIELDS_ID = "initialFieldsId";

interface InitialFieldsProps {
  alignment?: InitialFieldsAlignment;
  y?: number;
  setY: (y: number) => void;
  rank: number;
  isInitialFieldsActivated: boolean;
  pageNumber: number;
}

const InitialFields: React.FC<InitialFieldsProps> = ({
  alignment,
  y = 0,
  setY,
  rank,
  isInitialFieldsActivated,
  pageNumber,
}) => {
  const { t } = useTranslation();

  const draggableRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const [pdf, setPdf] = useState<HTMLCanvasElement | null>(null);
  const [initialFieldHeight, setInitialFieldHeight] = useState(0);

  useEffect(() => {
    if (draggableRef.current && initialFieldHeight === 0) {
      const initialFieldsBounds = draggableRef.current.getBoundingClientRect();
      setInitialFieldHeight(initialFieldsBounds.height);
    }
  });

  useLayoutEffect(() => {
    const queryPdf = document.querySelector<HTMLCanvasElement>(
      `#document_${rank} .pdf .active canvas`,
    );

    if (queryPdf) {
      setPdf(queryPdf);
    } else {
      const observer = new MutationObserver(() => {
        const observedPdf = document.querySelector<HTMLCanvasElement>(
          `#document_${rank} .pdf .active canvas`,
        );
        if (observedPdf) {
          setPdf(observedPdf);
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      observerRef.current = observer;
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [pdf, rank, pageNumber]);

  const correctAlignmentClassname = (alignment?: string) => {
    switch (alignment) {
      case "left":
        return "justify-content-start";
      case "center":
        return "justify-content-center";
      case "right":
        return "justify-content-end";
      default:
        return "";
    }
  };

  const onStop = () => {
    if (pdf && draggableRef.current) {
      const pdfBounds = pdf.getBoundingClientRect();
      const initialFieldsBounds = draggableRef.current.getBoundingClientRect();
      const positionY =
        pdfBounds.height +
        pdfBounds.top -
        initialFieldsBounds.top -
        initialFieldsBounds.height;
      defineUniversignPosition(pdf, pdfBounds, positionY);
    }
  };

  const defineUniversignPosition = (
    pdf: HTMLCanvasElement,
    bounds: DOMRect,
    positionY: number,
  ) => {
    const ratioY = positionY / bounds.height;
    const UNIVERSIGN_HEIGHT = pdf.height / UNIVERSIGN_RATIO;
    const adaptedHeight = Math.floor(UNIVERSIGN_HEIGHT * ratioY) + 1;
    setY(adaptedHeight >= 0 ? adaptedHeight : 0);
  };

  if (pdf) {
    const pdfBounds = pdf.getBoundingClientRect();

    const top =
      -pdfBounds.height -
      PDF_VIEWER_PAGINATION_MARGIN -
      initialFieldHeight +
      PDF_BORDER_SIZE;
    const bottom =
      -(PDF_VIEWER_PAGINATION_MARGIN + initialFieldHeight * 2) +
      PDF_BORDER_SIZE;

    const UNIVERSIGN_HEIGHT = pdf.height / UNIVERSIGN_RATIO;
    const ratioY = y / UNIVERSIGN_HEIGHT;
    const positionY = ratioY * pdfBounds.height;
    const position = { x: 0, y: Math.ceil(bottom - positionY) };

    const initialFieldsId = `${INITIAL_FIELDS_ID}_${rank}`;
    // ! Uncomment 1 and 2 if we remove from the CSP style-inline 'unsafe'
    /* 1
    const initialFieldsParentDiv = document.getElementById(initialFieldsId);
    initialFieldsParentDiv?.classList.add("dynamic-width");
    initialFieldsParentDiv?.style.setProperty(
      "--custom-width",
      `${pdfBounds.width}px`,
    );
    */

    return (
      <div
        id={initialFieldsId}
        className={
          `${isInitialFieldsActivated ? "d-flex" : "d-none"} flex-row position-absolute` /* 2 - dynamic-width */
        }
        style={{ width: `${pdfBounds.width}px` }}
      >
        <Draggable
          nodeRef={draggableRef}
          position={position}
          onStop={onStop}
          axis="y"
          bounds={{ top, bottom }}
        >
          <div
            ref={draggableRef}
            className={`px-4 w-100 d-flex rounded-4 border-dashed cursor-move ${correctAlignmentClassname(
              alignment,
            )}`}
          >
            {t("signature.positioning.initialFields.placeholder")}
          </div>
        </Draggable>
      </div>
    );
  }

  return null;
};

export default InitialFields;
